import { useEffect, useRef, useState } from 'react';
import { addComment, addVersion, latestVersion, listComments, publicImageUrl } from '../../lib/marketing';
import { useAuth } from '../../context/AuthContext';
import Badge from '../Badge';

const STATUS_TONE = { 'In Review': 'neutral', 'Needs Rework': 'danger', Approved: 'success' };

// The asset detail used to live inside the card in the grid, which meant a
// comment box, a file input and four actions all fighting for ~280px — the
// controls overflowed and the copy wrapped badly. A dialog gives the same
// content a two-column layout with room to breathe, and doubles as the
// click-to-enlarge view the grid thumbnails never had.
export default function AssetDetailModal({ asset, onClose, onSetStatus, onSetPinned, onDelete, onChanged }) {
  const { user } = useAuth();
  const closeRef = useRef(null);
  const [comments, setComments] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [reworkFile, setReworkFile] = useState(null);
  const [reworkCaption, setReworkCaption] = useState('');
  const [uploadingRework, setUploadingRework] = useState(false);

  const versions = asset.marketing_asset_versions || [];
  const current = latestVersion(asset);

  // Comments stay lazy — they're only fetched when a specific asset is
  // opened, never eagerly for the whole grid.
  useEffect(() => {
    listComments(asset.id).then((rows) => {
      setComments(rows);
      setCommentsLoaded(true);
    });
  }, [asset.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Stop the page behind the dialog scrolling under it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setPostingComment(true);
    try {
      const comment = await addComment(asset.id, user.id, newComment.trim());
      setComments((prev) => [...prev, comment]);
      setNewComment('');
    } finally {
      setPostingComment(false);
    }
  };

  const handleRework = async (e) => {
    e.preventDefault();
    if (!reworkFile) return;
    setUploadingRework(true);
    try {
      await addVersion(
        asset.id,
        current.version_number + 1,
        { caption: reworkCaption || current.caption, uploadedBy: user.id },
        reworkFile
      );
      setReworkFile(null);
      setReworkCaption('');
      await onChanged();
    } finally {
      setUploadingRework(false);
    }
  };

  if (!current) return null;

  const canPin = asset.channel === 'Instagram' && asset.status === 'Approved';

  return (
    <div className="asset-modal-backdrop" onClick={onClose}>
      <div
        className="asset-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Campaign asset detail"
        onClick={(e) => e.stopPropagation()}
      >
        <button ref={closeRef} className="asset-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="asset-modal-media">
          <img src={publicImageUrl(current.image_path)} alt={current.caption || 'Campaign asset'} />
          {versions.length > 1 && (
            <div className="asset-modal-versions">
              {versions
                .slice()
                .reverse()
                .map((v) => (
                  <div className="asset-modal-version" key={v.id} title={v.caption || ''}>
                    <img src={publicImageUrl(v.image_path)} alt={`Version ${v.version_number}`} />
                    <span>v{v.version_number}</span>
                  </div>
                ))}
            </div>
          )}
        </div>

        <div className="asset-modal-body">
          <div className="asset-modal-meta">
            <Badge tone={STATUS_TONE[asset.status]}>{asset.status}</Badge>
            {asset.channel && <Badge tone="accent">{asset.channel}</Badge>}
            {asset.pinned_at && <Badge tone="warning">📌 Pinned</Badge>}
            <span className="asset-modal-version-label">v{current.version_number}</span>
          </div>

          {current.caption && <p className="asset-modal-caption">{current.caption}</p>}

          <div className="asset-modal-actions">
            {asset.status !== 'Approved' && (
              <button className="asset-action asset-action-primary" onClick={() => onSetStatus(asset.id, 'Approved')}>
                Approve
              </button>
            )}
            {asset.status !== 'Needs Rework' && (
              <button className="asset-action" onClick={() => onSetStatus(asset.id, 'Needs Rework')}>
                Needs rework
              </button>
            )}
            {canPin && (
              <button className="asset-action" onClick={() => onSetPinned(asset.id, !asset.pinned_at)}>
                {asset.pinned_at ? 'Unpin from grid' : 'Pin to top'}
              </button>
            )}
            <button
              className="asset-action asset-action-danger"
              onClick={() => {
                onDelete(asset.id);
                onClose();
              }}
            >
              Delete
            </button>
          </div>

          <section className="asset-modal-section">
            <h4>Comments</h4>
            <div className="asset-modal-comments">
              {!commentsLoaded ? (
                <p className="asset-modal-muted">Loading…</p>
              ) : comments.length === 0 ? (
                <p className="asset-modal-muted">No comments yet.</p>
              ) : (
                comments.map((comment) => (
                  <div className="asset-comment" key={comment.id}>
                    <span className="asset-comment-author">
                      {comment.author?.display_name || comment.author?.email || 'Someone'}
                    </span>
                    <span className="asset-comment-body">{comment.body}</span>
                  </div>
                ))
              )}
            </div>
            <form className="asset-comment-form" onSubmit={handleAddComment}>
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment…"
              />
              <button type="submit" disabled={postingComment || !newComment.trim()}>
                Post
              </button>
            </form>
          </section>

          <section className="asset-modal-section">
            <h4>Rework / re-upload</h4>
            <form className="asset-rework-form" onSubmit={handleRework}>
              <label className="asset-file">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setReworkFile(e.target.files[0] || null)}
                />
                <span className="asset-file-button">Choose image</span>
                <span className="asset-file-name">{reworkFile ? reworkFile.name : 'No file chosen'}</span>
              </label>
              <input
                type="text"
                placeholder="New caption (optional — keeps the current one if blank)"
                value={reworkCaption}
                onChange={(e) => setReworkCaption(e.target.value)}
              />
              <button type="submit" disabled={!reworkFile || uploadingRework}>
                {uploadingRework ? 'Uploading…' : 'Upload new version'}
              </button>
            </form>
            <p className="asset-modal-hint">Uploading a new version sends the post back to In Review.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
